import { z } from "zod";
import { targetBoxSchema } from "../schemas/frontmatter/finding.js";

// ── Walkthrough Stream Events ────────────────────────────────

export const walkthroughTerminationReasonEnum = z.enum([
  "persona_done",
  "max_steps",
  "error",
  "cancelled",
]);

export type WalkthroughTerminationReason = z.infer<
  typeof walkthroughTerminationReasonEnum
>;

const stepFindingSchema = z.object({
  type: z.enum(["helps", "hinders", "missing", "irrelevant", "incorrect"]),
  severity: z.enum(["critical", "major", "minor", "nit"]),
  description: z.string(),
  recommendation: z.string().nullable().optional(),
  targetBox: targetBoxSchema.nullable().optional(),
});

export const walkthroughStreamEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("walkthrough_started"),
    walkthroughId: z.string(),
    mode: z.string(),
    runner: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .nullable()
      .optional(),
  }),
  z.object({
    type: z.literal("step_complete"),
    stepNumber: z.number(),
    title: z.string(),
    personaReasoning: z.string(),
    grounding: z.string().nullable(),
    screenshotPath: z.string().nullable(),
    action: z.string().nullable(),
    findings: z.array(stepFindingSchema),
  }),
  z.object({
    type: z.literal("action_executing"),
    stepNumber: z.number(),
    actionType: z.string(),
    selector: z.string().nullable(),
  }),
  z.object({
    type: z.literal("lens_analysis_started"),
    lensId: z.string(),
    lensLabel: z.string(),
  }),
  z.object({
    type: z.literal("lens_analysis_complete"),
    lensId: z.string(),
    lensLabel: z.string(),
    findingCount: z.number(),
  }),
  z.object({
    type: z.literal("lens_analysis_failed"),
    lensId: z.string(),
    lensLabel: z.string(),
    error: z.string(),
  }),
  z.object({
    type: z.literal("walkthrough_complete"),
    terminationReason: walkthroughTerminationReasonEnum,
    verdict: z
      .object({
        question: z.string(),
        answer: z.string(),
      })
      .nullable(),
    whatWorked: z.array(z.string()),
    openQuestions: z.array(z.string()),
  }),
  z.object({
    type: z.literal("walkthrough_error"),
    message: z.string(),
  }),
  // Local runs followed live (local-run-live-view): a preliminary finding
  // the walker just recorded, and the post-walk phase of the parent agent.
  z.object({
    type: z.literal("finding_recorded"),
    finding: z.object({
      id: z.string(),
      title: z.string(),
      type: stepFindingSchema.shape.type,
      severity: stepFindingSchema.shape.severity,
      uiFact: z.string().nullable(),
      consequence: z.string().nullable(),
      stepNumber: z.number().nullable(),
      taskImpact: z.string().nullable(),
      confidence: z.string().nullable(),
      recordedAt: z.string(),
      targetBox: targetBoxSchema.nullable().optional(),
    }),
  }),
  z.object({
    type: z.literal("phase_changed"),
    phase: z.enum([
      "walking",
      "completing",
      "matching",
      "judging",
      "publishing",
    ]),
  }),
]);

export type WalkthroughStreamEvent = z.infer<
  typeof walkthroughStreamEventSchema
>;

export type WalkthroughStreamCallback = (event: WalkthroughStreamEvent) => void;
